import { Button } from '@shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shared/components/ui/shadcn/dialog';
import { Label } from '@shared/components/ui/shadcn/label';
import { Input } from '@shared/components/ui/shadcn/input';
import { useToast } from '@shared/hooks/use-toast';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAddPayment } from '../../hooks';
import { AddPaymentRequest } from '../../models';

interface AddPaymentButtonProps {
  isOpen: boolean;
  onClose: () => void;
  fileId: number;
  fileStatus?: string;
}

export const AddPaymentButton = ({ isOpen, onClose, fileId, fileStatus }: AddPaymentButtonProps) => {
  const { t } = useTranslation('codCourierFileDetail');
  const [orderId, setOrderId] = useState('');
  const [amount, setAmount] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const { addPaymentAsync, isAdding } = useAddPayment(fileId);
  const { toast } = useToast();

  // Función helper para normalizar el estado
  const normalizeStatus = (status: string | undefined): string => {
    if (!status) return '';
    return status.toUpperCase();
  };

  const handleAddPayment = async () => {
    // Validar que el archivo esté en estado imported
    const normalizedStatus = normalizeStatus(fileStatus);
    if (normalizedStatus !== 'IMPORTED') {
      toast({
        title: t('addPayment.error.title', 'Error al añadir pago'),
        description: t(
          'addPayment.error.file_not_imported',
          'Solo se pueden añadir pagos a archivos en estado importado',
        ),
        variant: 'destructive',
      });
      return;
    }

    if (!orderId) {
      toast({
        title: t('addPayment.error.title', 'Error al añadir pago'),
        description: t('addPayment.error.order_required', 'Debe ingresar el ID del pedido'),
        variant: 'destructive',
      });
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      toast({
        title: t('addPayment.error.title', 'Error al añadir pago'),
        description: t('addPayment.error.amount_required', 'Debe ingresar un importe válido mayor a 0'),
        variant: 'destructive',
      });
      return;
    }

    try {
      const data: AddPaymentRequest = {
        order_id: parseInt(orderId, 10),
        amount: parseFloat(amount),
        ...(trackingNumber ? { tracking_number: trackingNumber } : {}),
      };

      await addPaymentAsync(data);

      onClose();
      setOrderId('');
      setAmount('');
      setTrackingNumber('');
    } catch {
      // El error ya se maneja en el hook
    }
  };

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setOrderId('');
      setAmount('');
      setTrackingNumber('');
    }
  }, [isOpen]);

  const handleClose = () => {
    if (!isAdding) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('addPayment.modal_title', 'Añadir Pago al Archivo')}</DialogTitle>
          <DialogDescription>
            {t('addPayment.modal_description', 'Añade un pago manual a este archivo de courier.')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* ID del Pedido */}
          <div className="space-y-2">
            <Label htmlFor="order_id">{t('addPayment.order_id', 'ID del Pedido')} *</Label>
            <Input
              id="order_id"
              type="number"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              placeholder={t('addPayment.order_id_placeholder', 'Ej: 12345')}
              disabled={isAdding}
            />
          </div>

          {/* Importe */}
          <div className="space-y-2">
            <Label htmlFor="amount">{t('addPayment.amount', 'Importe')} *</Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              min="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={t('addPayment.amount_placeholder', 'Ej: 100.00')}
              disabled={isAdding}
            />
          </div>

          {/* Tracking Number */}
          <div className="space-y-2">
            <Label htmlFor="tracking_number">
              {t('addPayment.tracking_number', 'Número de Seguimiento')} ({t('addPayment.optional', 'Opcional')})
            </Label>
            <Input
              id="tracking_number"
              type="text"
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              placeholder={t('addPayment.tracking_number_placeholder', 'Ej: TRACK123')}
              disabled={isAdding}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isAdding}>
            {t('addPayment.cancel', 'Cancelar')}
          </Button>
          <Button
            onClick={handleAddPayment}
            disabled={
              isAdding || !orderId || !amount || parseFloat(amount) <= 0 || normalizeStatus(fileStatus) !== 'IMPORTED'
            }
          >
            {isAdding ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('addPayment.adding', 'Añadiendo...')}
              </>
            ) : (
              t('addPayment.confirm', 'Añadir Pago')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
